import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import {
  DirectionEnum,
  PivotAggregationEnum,
  PivotDateGroupEnum,
  PivotFilterOperatorEnum,
  PivotGrandTotalsEnum,
  PivotNegativeNumberFormatEnum,
  PivotRelativeUnitEnum,
  PivotTextAlignEnum,
} from '@ztechapps/common.contract'
import type {
  PivotCondition,
  PivotLayout,
  PivotLayoutColumnWidth,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMemberSort,
  PivotLayoutValueColumn,
  PivotLayoutValueSort,
  PivotMeasureFormat,
  PivotValueComparison,
} from '@ztechapps/common.contract'
import { isPivotRangeOperator } from '../is-pivot-range-operator'
import { isPivotRelativeOperator } from '../is-pivot-relative-operator'
import { isPivotTopOrBottomOperator } from '../is-pivot-top-or-bottom-operator'
import { isRecord } from '../is-record'
import { PIVOT_FORMULA_FUNCTIONS } from '../pivot-formula-functions'
import { PIVOT_VALUE_COMPARISON_OPERATORS } from '../pivot-value-comparison-operators'

const MEASURES_PLACEHOLDERS = ['Measures', '[Measures]']
const DATE_SUFFIX_TO_GROUP: Array<{ suffix: string; dateGroup?: PivotDateGroupEnum }> = [
  { suffix: '.Year', dateGroup: PivotDateGroupEnum.Year },
  { suffix: '.Quarter', dateGroup: PivotDateGroupEnum.Quarter },
  { suffix: '.Month', dateGroup: PivotDateGroupEnum.Month },
  { suffix: '.Day' },
]
const CARRIED_FORMAT_KEYS = [
  'name',
  'decimalPlaces',
  'currencySymbol',
  'thousandsSeparator',
  'decimalSeparator',
  'negativeNumberFormat',
  'isPercent',
  'textAlign',
  'nullValue',
]
const FORMULA_OPERATOR_WORDS = ['and', 'or']

export type FlexmonsterConversionCounts = {
  measuresDropped: number
  filtersDropped: number
  formatPropertiesDropped: number
  sortsDropped: number
  widthsDropped: number
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/** `<field>.Year/.Quarter/.Month/.Day` → the bare field plus its date grouping (Day = none). */
function mapField(uniqueName: string): PivotLayoutField {
  for (const { suffix, dateGroup } of DATE_SUFFIX_TO_GROUP) {
    if (uniqueName.endsWith(suffix)) {
      const field = uniqueName.slice(0, -suffix.length)
      return dateGroup ? { field, dateGroup } : { field }
    }
  }
  return { field: uniqueName }
}

/** A field key carrying a date part (`<field>.Year` / `.Quarter` / `.Month` / `.Day`). */
function isDatePartField(uniqueName: string): boolean {
  return DATE_SUFFIX_TO_GROUP.some(({ suffix }) => uniqueName.endsWith(suffix))
}

function mapAggregation(aggregation: unknown): PivotAggregationEnum | undefined {
  switch (typeof aggregation === 'string' ? aggregation.toLowerCase() : '') {
    case 'sum':
      return PivotAggregationEnum.Sum
    case 'count':
      return PivotAggregationEnum.Count
    case 'distinctcount':
      return PivotAggregationEnum.DistinctCount
    case 'average':
      return PivotAggregationEnum.Average
    case 'max':
      return PivotAggregationEnum.Max
    case 'min':
      return PivotAggregationEnum.Min
    default:
      return undefined
  }
}

/** A Flexmonster `asc`/`desc` marker → the pivot sort direction; anything else is dropped. */
function mapSortDirection(type: unknown): DirectionEnum | undefined {
  switch (typeof type === 'string' ? type.toLowerCase() : '') {
    case 'asc':
      return DirectionEnum.asc
    case 'desc':
      return DirectionEnum.desc
    default:
      return undefined
  }
}

/** A formula the pivot evaluates: non-empty, no bare `!`, and only known function names outside quoted field names. */
function isSupportedFormula(formula: string): boolean {
  if (isEmpty(formula.trim())) {
    return false
  }
  if (/!(?!=)/.test(formula)) {
    return false
  }
  const names = formula.replace(/"[^"]*"|'[^']*'/g, '').match(/[A-Za-z_][A-Za-z0-9_]*(?=\s*\()/g) ?? []
  return names
    .filter((name) => !FORMULA_OPERATOR_WORDS.includes(name))
    .every((name) => PIVOT_FORMULA_FUNCTIONS.includes(name))
}

/** A Flexmonster grand-totals marker → the pivot setting; anything else (or absent) shows all totals. */
function mapGrandTotals(value: unknown): PivotGrandTotalsEnum {
  switch (typeof value === 'string' ? value.toLowerCase() : '') {
    case 'off':
      return PivotGrandTotalsEnum.Off
    case 'rows':
      return PivotGrandTotalsEnum.Rows
    case 'columns':
      return PivotGrandTotalsEnum.Columns
    default:
      return PivotGrandTotalsEnum.On
  }
}

/**
 * One `formats[]` entry → the measure `format`. Only the eight carried keys map; `name` is the
 * lookup key. `decimalPlaces` below zero (or not a number) and any other `textAlign` value are
 * dropped and counted, as is every property outside the carried set.
 */
function mapFormatEntry(entry: unknown): { format: PivotMeasureFormat; dropped: number } {
  const format: PivotMeasureFormat = {}
  let dropped = 0
  if (!isRecord(entry)) {
    return { format, dropped }
  }
  const source = entry

  if (source.decimalPlaces !== undefined) {
    if (typeof source.decimalPlaces === 'number' && source.decimalPlaces >= 0) {
      format.decimalPlaces = Math.min(source.decimalPlaces, 10)
    } else {
      dropped += 1
    }
  }
  if (typeof source.currencySymbol === 'string' && !isEmpty(source.currencySymbol)) {
    format.currencySymbol = source.currencySymbol
  }
  if (typeof source.thousandsSeparator === 'string') {
    format.thousandsSeparator = source.thousandsSeparator
  }
  if (source.decimalSeparator !== undefined) {
    if (typeof source.decimalSeparator === 'string' && !isEmpty(source.decimalSeparator)) {
      format.decimalSeparator = source.decimalSeparator
    } else {
      dropped += 1
    }
  }
  if (source.negativeNumberFormat !== undefined) {
    if (includes(PivotNegativeNumberFormatEnum, source.negativeNumberFormat)) {
      format.negativeNumberFormat = source.negativeNumberFormat as PivotNegativeNumberFormatEnum
    } else {
      dropped += 1
    }
  }
  if (typeof source.isPercent === 'boolean') {
    format.isPercent = source.isPercent
  }
  if (source.textAlign !== undefined) {
    switch (typeof source.textAlign === 'string' ? source.textAlign.toLowerCase() : '') {
      case 'left':
        format.textAlign = PivotTextAlignEnum.Left
        break
      case 'center':
        format.textAlign = PivotTextAlignEnum.Center
        break
      case 'right':
        format.textAlign = PivotTextAlignEnum.Right
        break
      default:
        dropped += 1
    }
  }
  if (typeof source.nullValue === 'string' && !isEmpty(source.nullValue)) {
    format.nullValue = source.nullValue
  }
  for (const key of Object.keys(source)) {
    if (!CARRIED_FORMAT_KEYS.includes(key)) {
      dropped += 1
    }
  }
  return { format, dropped }
}

/**
 * `status.Active` → `Active`, `country.[united states]` → `united states`. The prefix matches
 * regardless of case: a legacy member spells the field in its own case (`isinvalid.[false]`
 * under the field `IsInvalid`).
 */
function stripMember(member: string, prefix: string): string {
  const withoutPrefix = member.toLowerCase().startsWith(prefix.toLowerCase()) ? member.slice(prefix.length) : member
  return withoutPrefix.startsWith('[') && withoutPrefix.endsWith(']') && withoutPrefix.length >= 2
    ? withoutPrefix.slice(1, -1)
    : withoutPrefix
}

type ColumnAxisEntry = { uniqueName: string; field: PivotLayoutField }

/**
 * A Flexmonster `tuple` + `measure` pair → the pivot value column. `tuple[i]` names a member
 * of the `i`-th Columns field (`<uniqueName>.<member>`); an empty tuple is the Grand Total
 * column. Anything unresolvable returns `undefined` so the caller drops and counts it.
 */
function resolveValueColumn(
  tuple: unknown,
  measure: unknown,
  columnAxis: ColumnAxisEntry[]
): PivotLayoutValueColumn | undefined {
  if (!Array.isArray(tuple) || tuple.length > columnAxis.length) {
    return undefined
  }
  const members = tuple.filter((member): member is string => typeof member === 'string')
  if (members.length !== tuple.length) {
    return undefined
  }
  if (!isRecord(measure) || typeof measure.uniqueName !== 'string') {
    return undefined
  }
  const aggregation = mapAggregation(measure.aggregation)
  if (!aggregation) {
    return undefined
  }
  if (!members.every((member, level) => member.startsWith(`${columnAxis[level].uniqueName}.`))) {
    return undefined
  }
  return {
    columnPath: members.map((member, level) => ({
      ...columnAxis[level].field,
      key: stripMember(member, `${columnAxis[level].uniqueName}.`),
    })),
    measure: { field: measure.uniqueName, aggregation },
  }
}

/**
 * A Flexmonster `query` object → the Condition it states. One operator key of the Condition set maps to it: a
 * relative key takes a unit name, `between`/`not_between` a pair, any other a single operand. `top`/`bottom` (a Value
 * query), several keys and a malformed operand return `undefined` so the caller drops and counts the filter.
 */
function mapQuery(query: unknown): PivotCondition | undefined {
  if (!isRecord(query)) {
    return undefined
  }
  const keys = Object.keys(query)
  const operator = Object.values(PivotFilterOperatorEnum).find((value) => value === keys[0])
  if (keys.length !== 1 || !operator || isPivotTopOrBottomOperator(operator)) {
    return undefined
  }
  const operand = query[operator]
  if (isPivotRelativeOperator(operator)) {
    const unit = Object.values(PivotRelativeUnitEnum).find((value) => value === operand)
    return unit ? { operator, values: [], unit } : undefined
  }
  const isOperand = (value: unknown): value is string | number => typeof value === 'string' || typeof value === 'number'
  if (isPivotRangeOperator(operator)) {
    return Array.isArray(operand) && operand.length === 2 && operand.every(isOperand)
      ? { operator, values: operand.map(String) }
      : undefined
  }
  return isOperand(operand) ? { operator, values: [String(operand)] } : undefined
}

/**
 * A Flexmonster value-comparison `filter` (a `measure` and a `query`) → the Value comparison it states. The measure
 * must be an aggregated entry of the slice's measures, and the query one of the eight comparison keys; anything else
 * returns `undefined` so the caller drops and counts the filter.
 */
function mapValueComparison(filter: Record<string, unknown>, measures: unknown[]): PivotValueComparison | undefined {
  const condition = mapQuery(filter.query)
  const uniqueName = isRecord(filter.measure) ? filter.measure.uniqueName : undefined
  const measure = measures.find((entry) => isRecord(entry) && entry.uniqueName === uniqueName)
  const aggregation = isRecord(measure) ? mapAggregation(measure.aggregation) : undefined
  if (!condition || !PIVOT_VALUE_COMPARISON_OPERATORS.includes(condition.operator) || !aggregation) {
    return undefined
  }
  return {
    measure: { field: String(uniqueName), aggregation },
    operator: condition.operator,
    values: condition.values,
  }
}

/**
 * A `filter` object → the kept values. `members`/`exclude` map to
 * `includedValues`/`excludedValues`, a lone `query` to `conditionFilter` and a `measure` with a `query` to
 * `valueComparison`; a filter using anything else keeps no values and counts as dropped. No filter (or an empty one) keeps no values without counting as dropped.
 */
function mapFilter(
  filter: unknown,
  prefix: string,
  measures: unknown[]
): Pick<PivotLayoutFilter, 'includedValues' | 'excludedValues' | 'conditionFilter' | 'valueComparison'> & {
  dropped: boolean
} {
  if (!isRecord(filter)) {
    return { dropped: false }
  }
  const source = filter
  if (Object.keys(source).length === 1 && 'query' in source) {
    const conditionFilter = mapQuery(source.query)
    return conditionFilter ? { conditionFilter, dropped: false } : { dropped: true }
  }
  if (Object.keys(source).length === 2 && 'measure' in source && 'query' in source) {
    const valueComparison = mapValueComparison(source, measures)
    return valueComparison ? { valueComparison, dropped: false } : { dropped: true }
  }
  if (Object.keys(source).some((key) => key !== 'members' && key !== 'exclude')) {
    return { dropped: true }
  }
  const values: Pick<PivotLayoutFilter, 'includedValues' | 'excludedValues'> = {}
  if (Array.isArray(source.members)) {
    values.includedValues = source.members.map((member) => stripMember(String(member), prefix))
  }
  if (Array.isArray(source.exclude)) {
    values.excludedValues = source.exclude.map((member) => stripMember(String(member), prefix))
  }
  return { ...values, dropped: false }
}

export function convertFlexmonsterLayout(
  report: Record<string, unknown>,
  counts: FlexmonsterConversionCounts
): PivotLayout {
  const slice = isRecord(report.slice) ? report.slice : {}

  const formatEntries = asArray(report.formats).filter(isRecord)
  const mappedFormats = formatEntries.map((entry) => {
    const { format, dropped } = mapFormatEntry(entry)
    counts.formatPropertiesDropped += dropped
    return { name: entry.name, format }
  })
  const baseFormat = mappedFormats.find((entry) => entry.name === '')?.format ?? {}

  const filters: PivotLayoutFilter[] = []
  const rows: PivotLayoutField[] = []
  const columns: PivotLayoutField[] = []
  const memberSorts: PivotLayoutMemberSort[] = []
  const columnAxis: ColumnAxisEntry[] = []
  for (const [entries, zone] of [
    [asArray(slice.rows), rows],
    [asArray(slice.columns), columns],
  ] as const) {
    for (const axisEntry of entries) {
      if (!isRecord(axisEntry) || typeof axisEntry.uniqueName !== 'string') {
        continue
      }
      if (MEASURES_PLACEHOLDERS.includes(axisEntry.uniqueName)) {
        continue
      }
      if (zone === columns) {
        columnAxis.push({ uniqueName: axisEntry.uniqueName, field: mapField(axisEntry.uniqueName) })
      }
      if (axisEntry.sort !== undefined) {
        const direction = mapSortDirection(axisEntry.sort)
        if (direction) {
          memberSorts.push({ ...mapField(axisEntry.uniqueName), direction })
        } else {
          counts.sortsDropped += 1
        }
      }
      const axisFilter = axisEntry.filter
      if (isDatePartField(axisEntry.uniqueName)) {
        if (isRecord(axisFilter) && ('members' in axisFilter || 'exclude' in axisFilter)) {
          counts.filtersDropped += 1
        }
      } else if (isRecord(axisFilter)) {
        const { dropped, ...values } = mapFilter(axisFilter, `${axisEntry.uniqueName}.`, asArray(slice.measures))
        if (dropped) {
          counts.filtersDropped += 1
        } else if (values.includedValues || values.excludedValues || values.conditionFilter || values.valueComparison) {
          filters.push({ ...mapField(axisEntry.uniqueName), ...values })
        }
      }
      zone.push(mapField(axisEntry.uniqueName))
    }
  }

  const measures: PivotLayout['measures'] = []
  for (const measureEntry of asArray(slice.measures)) {
    if (!isRecord(measureEntry) || typeof measureEntry.uniqueName !== 'string') {
      continue
    }
    const source = measureEntry
    if (typeof source.formula === 'string') {
      const namedFormat =
        typeof source.format === 'string' && source.format !== ''
          ? (mappedFormats.find((entry) => entry.name === source.format)?.format ?? {})
          : {}
      const format = { ...baseFormat, ...namedFormat }
      if (isSupportedFormula(source.formula)) {
        measures.push({
          field: measureEntry.uniqueName,
          formula: source.formula,
          ...(source.individual === true ? { individual: true } : {}),
          ...(isEmpty(format) ? {} : { format }),
        })
      } else {
        counts.measuresDropped += 1
      }
      continue
    }
    const aggregation = mapAggregation(source.aggregation)
    if (!aggregation) {
      counts.measuresDropped += 1
      continue
    }
    const namedFormat =
      typeof source.format === 'string' && source.format !== ''
        ? (mappedFormats.find((entry) => entry.name === source.format)?.format ?? {})
        : {}
    const format = { ...baseFormat, ...namedFormat }
    measures.push(
      isEmpty(format)
        ? { field: measureEntry.uniqueName, aggregation }
        : { field: measureEntry.uniqueName, aggregation, format }
    )
  }

  for (const reportFilter of asArray(slice.reportFilters)) {
    if (!isRecord(reportFilter) || typeof reportFilter.uniqueName !== 'string') {
      continue
    }
    const source = reportFilter
    if (isDatePartField(reportFilter.uniqueName)) {
      if (isRecord(source.filter) && ('members' in source.filter || 'exclude' in source.filter)) {
        counts.filtersDropped += 1
      }
      filters.push(mapField(reportFilter.uniqueName))
      continue
    }
    const { dropped, ...values } = mapFilter(source.filter, `${reportFilter.uniqueName}.`, [])
    if (dropped) {
      counts.filtersDropped += 1
    }
    filters.push({ ...mapField(reportFilter.uniqueName), ...values })
  }

  const grid = isRecord(report.options) && isRecord(report.options.grid) ? report.options.grid : {}
  const expands = isRecord(slice.expands) ? slice.expands : {}
  const isFlat = grid.type === 'flat'

  const sorting = isRecord(slice.sorting) ? slice.sorting : {}
  let valueSort: PivotLayoutValueSort | undefined
  if (isRecord(sorting.column)) {
    const direction = mapSortDirection(sorting.column.type)
    const valueColumn = resolveValueColumn(sorting.column.tuple, sorting.column.measure, columnAxis)
    if (direction && valueColumn) {
      valueSort = { ...valueColumn, direction }
    } else {
      counts.sortsDropped += 1
    }
  }
  if (sorting.row !== undefined) {
    counts.sortsDropped += 1
  }

  const tableSizes = isRecord(report.tableSizes) ? report.tableSizes : {}
  const columnWidths: PivotLayoutColumnWidth[] = []
  for (const sizeEntry of asArray(tableSizes.columns)) {
    if (!isRecord(sizeEntry) || typeof sizeEntry.width !== 'number') {
      counts.widthsDropped += 1
      continue
    }
    if (
      typeof sizeEntry.idx === 'number' &&
      Number.isInteger(sizeEntry.idx) &&
      sizeEntry.idx >= 0 &&
      sizeEntry.idx < rows.length &&
      (grid.type === 'classic' || isFlat)
    ) {
      columnWidths.push({ rowField: rows[sizeEntry.idx], width: sizeEntry.width })
      continue
    }
    const valueColumn = resolveValueColumn(sizeEntry.tuple, sizeEntry.measure, columnAxis)
    if (valueColumn) {
      columnWidths.push({ valueColumn, width: sizeEntry.width })
    } else {
      counts.widthsDropped += 1
    }
  }
  counts.widthsDropped += asArray(tableSizes.rows).length

  return {
    rows,
    columns,
    measures,
    filters,
    memberSorts,
    valueSort,
    columnWidths,
    showSubtotals: isFlat ? false : grid.showTotals !== 'off',
    showGrandTotals: mapGrandTotals(grid.showGrandTotals),
    expandAll: expands.expandAll === true || isFlat,
  }
}
